import { useState } from 'react';
import { Lock, Fingerprint, Clock, Check, Loader2 } from 'lucide-react';
import PageHeader from '../../components/settings/PageHeader';
import SettingCard from '../../components/settings/SettingCard';
import Toggle from '../../components/settings/Toggle';
import Segmented from '../../components/settings/Segmented';
import { useSettingsStore } from '../../store/settingsStore';
import { cn } from '../../utils/helpers';

const TIMEOUTS = [
  { value: '0', label: 'Immediately' },
  { value: '1', label: '1 min' },
  { value: '5', label: '5 min' },
  { value: '15', label: '15 min' },
];

export default function AppLock() {
  const { data, saveSecurity, saving } = useSettingsStore();
  const sec = data?.security;

  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinSet, setPinSet] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!sec) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-400 dark:text-neutral-500">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const enable = async () => {
    if (pin.length < 4) {
      setError('Choose a passcode of at least 4 digits.');
      return;
    }
    if (pin !== confirmPin) {
      setError('Passcodes do not match.');
      return;
    }
    setError(null);
    // NOTE: a real implementation stores only a salted hash of the passcode.
    await saveSecurity({ appLockEnabled: true });
    setPinSet(true);
    setPin('');
    setConfirmPin('');
  };

  const disable = async () => {
    await saveSecurity({ appLockEnabled: false });
    setPinSet(false);
  };

  return (
    <div className="space-y-6 max-w-xl">
      <PageHeader title="App Lock" subtitle="Require a passcode or biometrics to open Messego" icon={Lock} backTo="/app/settings/security" />

      <SettingCard>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className={cn('w-11 h-11 rounded-2xl flex items-center justify-center', sec.appLockEnabled ? 'bg-green-50 text-green-600' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400')}>
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold">{sec.appLockEnabled ? 'App Lock is on' : 'App Lock is off'}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{sec.appLockEnabled ? `Locks after ${sec.appLockTimeout} min of inactivity` : 'Anyone with your device can open Messego'}</p>
            </div>
          </div>
          {sec.appLockEnabled && (
            <button onClick={disable} disabled={saving} className="text-xs font-semibold text-rose-600 dark:text-rose-400 rounded-xl px-3 py-2 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors disabled:opacity-50">
              Turn off
            </button>
          )}
        </div>
      </SettingCard>

      {!sec.appLockEnabled ? (
        <SettingCard title="Set a passcode">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-neutral-700">Passcode</label>
              <input value={pin} onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, '').slice(0, 6))} inputMode="numeric" type="password" placeholder="4–6 digits" className="w-full mt-1 px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 tracking-[0.4em] font-mono focus:outline-none focus:ring-2 focus:ring-neutral-900/10" />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700">Confirm passcode</label>
              <input value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/[^\d]/g, '').slice(0, 6))} inputMode="numeric" type="password" className="w-full mt-1 px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 tracking-[0.4em] font-mono focus:outline-none focus:ring-2 focus:ring-neutral-900/10" />
            </div>
            {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}
            <button onClick={enable} disabled={saving} className="w-full bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors disabled:opacity-60">
              {saving ? 'Enabling…' : 'Enable App Lock'}
            </button>
          </div>
        </SettingCard>
      ) : (
        <>
          {pinSet && (
            <div className="flex items-center gap-2 rounded-2xl bg-green-50 text-green-700 text-sm px-4 py-3">
              <Check className="w-4 h-4" /> Passcode set successfully.
            </div>
          )}
          <SettingCard title="Lock options" flush>
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800">
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><Clock className="w-5 h-5" /></div>
                <div><p className="font-semibold text-sm">Auto-lock after</p><p className="text-xs text-neutral-500 dark:text-neutral-400">Inactivity before locking</p></div>
              </div>
              <Segmented
                value={String(sec.appLockTimeout)}
                onChange={(v) => saveSecurity({ appLockTimeout: Number(v) })}
                options={TIMEOUTS}
              />
            </div>
            <div className="flex items-center justify-between gap-4 px-5 py-4">
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300"><Fingerprint className="w-5 h-5" /></div>
                <div><p className="font-semibold text-sm">Biometric unlock</p><p className="text-xs text-neutral-500 dark:text-neutral-400">Use Face ID / fingerprint</p></div>
              </div>
              <Toggle checked={sec.biometricEnabled} onChange={(v) => saveSecurity({ biometricEnabled: v })} label="Biometric unlock" />
            </div>
          </SettingCard>
        </>
      )}
    </div>
  );
}
